import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import "./Navbar.css";
import Footer from "./Footer";
import { FaBars, FaTimes } from "react-icons/fa";

const logo = require("./assets/images/iithlogo_footer.png");
const MOBILE_NAV_BREAKPOINT = 1240;

const Navbar = () => {
  const location = useLocation();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [mobileHomeSubMenuOpen, setMobileHomeSubMenuOpen] = useState(false);
  const [mobilePeopleSubMenuOpen, setMobilePeopleSubMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'home' | 'people' | null

  // Scroll to top on every page change, but NOT on hash-only changes
  // (hash navigation is handled separately by HashScroll / HomeText)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname]);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setMobileHomeSubMenuOpen(false);
    setMobilePeopleSubMenuOpen(false);
  };

  const toggleMenu = () => {
    const nextState = !isMenuOpen;
    setMenuOpen(nextState);
    if (!nextState) {
      setMobileHomeSubMenuOpen(false);
      setMobilePeopleSubMenuOpen(false);
    }
  };

  const toggleMobileHomeSubMenu = () => {
    setMobileHomeSubMenuOpen((open) => !open);
  };

  const toggleMobilePeopleSubMenu = () => {
    setMobilePeopleSubMenuOpen((open) => !open);
  };

  const handleLinkClick = () => {
    closeMobileMenu();
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMenuOpen]);
  

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > MOBILE_NAV_BREAKPOINT) {
        closeMobileMenu();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const programsHref = "/programs";

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-container">
          {/* Left: Desktop logo / Mobile title */}
          <div className="navbar-left">
            <Link
              to="/"
              className="navbar-brand"
              onClick={closeMobileMenu}
              aria-label="IIT Hyderabad home"
            >
              <img src={logo} alt="IIT Hyderabad logo" className="navbar-brand-logo" />
            </Link>
            <div className="navbar-brand-text">
              Centre for Interdisciplinary Programs
            </div>
          </div>
          {/* Center: Desktop Menu */}
          <ul className="navbar-menu">
            <li
              className="navbar-item dropdown"
              onMouseEnter={() => setOpenDropdown('home')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link to="/" onClick={closeMobileMenu}>
                Home <span className="dropdown-arrow">▼</span>
              </Link>
              <ul className={`dropdown-menu${openDropdown === 'home' ? ' open' : ''}`}>
                <li>
                  <Link to="/#about" onClick={() => setOpenDropdown(null)}>About</Link>
                </li>
                <li>
                  <Link to="/#announcements" onClick={() => setOpenDropdown(null)}>Announcements</Link>
                </li>
                <li>
                  <Link to="/#admissions" onClick={() => setOpenDropdown(null)}>Admissions</Link>
                </li>
                <li>
                  <Link to="/#highlights" onClick={() => setOpenDropdown(null)}>Highlights</Link>
                </li>
              </ul>
            </li>
            <li className="navbar-item dropdown">
              <Link to={programsHref} onClick={closeMobileMenu}>
                Programs
              </Link>
            </li>
            <li className="navbar-item">
              <Link to="/research" onClick={closeMobileMenu}>
                Research
              </Link>
            </li>
            <li className="navbar-item">
              <Link to="/#news-events" onClick={closeMobileMenu}>
                News & Events
              </Link>
            </li>
            <li className="navbar-item">
              <Link to="/outreach-funding" onClick={closeMobileMenu}>
                Outreach & Funding
              </Link>
            </li>
            <li className="navbar-item">
              <Link to="/forms" onClick={closeMobileMenu}>
                Forms
              </Link>
            </li>
            <li
              className="navbar-item dropdown"
              onMouseEnter={() => setOpenDropdown('people')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link to="/people" onClick={closeMobileMenu}>
                People <span className="dropdown-arrow">▼</span>
              </Link>
              <ul className={`dropdown-menu${openDropdown === 'people' ? ' open' : ''}`}>
                <li>
                  <Link to="/people/staff" onClick={() => setOpenDropdown(null)}>
                    Staff
                  </Link>
                </li>
                <li>
                  <Link to="/people/pg-students" onClick={() => setOpenDropdown(null)}>
                    PG Students
                  </Link>
                </li>
                <li>
                  <Link to="/people/phd-students" onClick={() => setOpenDropdown(null)}>
                    PhD Students
                  </Link>
                </li>
                <li>
                  <Link to="/people/alumni" onClick={() => setOpenDropdown(null)}>
                    Alumni
                  </Link>
                </li>
              </ul>
            </li>
            <li className="navbar-item">
              <Link to="/contactus" onClick={closeMobileMenu}>
                Contact Us
              </Link>
            </li>
          </ul>
          {/* Hamburger Icon for Mobile */}
          <button
            className="navbar-toggle"
            type="button"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-controls="mobile-navigation"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
        {/* Mobile Menu */}
        <div
          id="mobile-navigation"
          className={`mobile-menu ${isMenuOpen ? "open" : ""}`}
        >
          <div className="mobile-menu-header">
            <div className="mobile-menu-brand">
              Centre for Interdisciplinary Programs
            </div>
            <button
              className="mobile-menu-close"
              type="button"
              onClick={toggleMenu}
              aria-label="Close menu"
            >
              &times;
            </button>
          </div>
          <ul className="mobile-menu-list">
            <li className="mobile-menu-item">
              <button
                type="button"
                className="mobile-submenu-toggle"
                onClick={toggleMobileHomeSubMenu}
                aria-expanded={mobileHomeSubMenuOpen}
              >
                Home{" "}
                <span className="dropdown-arrow">
                  {mobileHomeSubMenuOpen ? "▲" : "▼"}
                </span>
              </button>
              {mobileHomeSubMenuOpen && (
                <ul className="mobile-dropdown-menu">
                  <li>
                    <Link to="/#about" onClick={handleLinkClick}>
                      About
                    </Link>
                  </li>
                  <li>
                    <Link to="/#announcements" onClick={handleLinkClick}>
                      Announcements
                    </Link>
                  </li>
                  <li>
                    <Link to="/#admissions" onClick={handleLinkClick}>
                      Admissions
                    </Link>
                  </li>
                  <li>
                    <Link to="/#highlights" onClick={handleLinkClick}>
                      Highlights
                    </Link>
                  </li>
                </ul>
              )}
            </li>
            <li className="mobile-menu-item">
              <Link to={programsHref} onClick={handleLinkClick}>
                Programs
              </Link>
            </li>
            <li className="mobile-menu-item">
              <Link to="/research" onClick={handleLinkClick}>
                Research
              </Link>
            </li>
            <li className="mobile-menu-item">
              <Link to="/#news-events" onClick={handleLinkClick}>
                News & Events
              </Link>
            </li>
            <li className="mobile-menu-item">
              <Link to="/outreach-funding" onClick={handleLinkClick}>
                Outreach & Funding
              </Link>
            </li>
            <li className="mobile-menu-item">
              <Link to="/forms" onClick={handleLinkClick}>
                Forms
              </Link>
            </li>
            <li className="mobile-menu-item">
              <button
                type="button"
                className="mobile-submenu-toggle"
                onClick={toggleMobilePeopleSubMenu}
                aria-expanded={mobilePeopleSubMenuOpen}
              >
                People{" "}
                <span className="dropdown-arrow">
                  {mobilePeopleSubMenuOpen ? "▲" : "▼"}
                </span>
              </button>
              {mobilePeopleSubMenuOpen && (
                <ul className="mobile-dropdown-menu">
                  <li>
                    <Link to="/people/staff" onClick={handleLinkClick}>
                      Staff
                    </Link>
                  </li>
                  <li>
                    <Link to="/people/pg-students" onClick={handleLinkClick}>
                      PG Students
                    </Link>
                  </li>
                  <li>
                    <Link to="/people/phd-students" onClick={handleLinkClick}>
                      PhD Students
                    </Link>
                  </li>
                  <li>
                    <Link to="/people/alumni" onClick={handleLinkClick}>
                      Alumni
                    </Link>
                  </li>
                </ul>
              )}
            </li>
            <li className="mobile-menu-item">
              <Link to="/contactus" onClick={handleLinkClick}>
                Contact Us
              </Link>
            </li>
          </ul>
        </div>
        {/* Optional overlay for closing mobile menu */}
        {isMenuOpen && (
          <div className="menu-overlay" onClick={toggleMenu}></div>
        )}
      </nav>
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Navbar;
